import { DATE_FORMAT_REQUEST } from 'data/constants';
import moment from 'moment';
import { z } from 'zod';
import { signUpTexts } from './texts';

const {
  firstName_input_error_warning,
  lastName_input_error_warning,
  email_input_error_warning,
  password_input_error_warning,
  cityOfResidence_id_input_error,
  phoneNumber_input_error_warning,
  dateOfBirth_input_error_warning,
  confirmPassword_input_error_warning,
  termsAccept_input_error_warning,
  child_name_input_error_warning,
  child_name_max_age_error_warning,
  radio_input_error_warning,
  instagram_username_input_error_warning,
  credit_card_name_input_error_warning,
  credit_card_number_input_error_warning,
  credit_card_expiry_date_input_error_warning,
} = signUpTexts;

const signUpStep1Schema = z.object({
  currency: z.string(),
  memberShipCategory: z.literal('Leisure').or(z.literal('Corporate')),
  membershipId: z.string().nullable(),
});

const emailSchema = z.string().email({ message: email_input_error_warning });

const dobSuperRefinement =
  ({ isOptional = true, maxAge = 200, maxAgeErrorMsg = '' } = {}) =>
  (dob: string, ctx: z.RefinementCtx) => {
    if (isOptional && !dob) {
      return z.NEVER;
    }

    const dateOfBirth = moment(dob, DATE_FORMAT_REQUEST, true);

    if (!dateOfBirth.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: dateOfBirth_input_error_warning,
      });

      return z.NEVER;
    }

    if (!maxAgeErrorMsg) {
      return z.NEVER;
    }

    const today = moment();
    const maxAgeYearsAgo = moment()
      .subtract(maxAge, 'years')
      .subtract(1, 'day');

    const isMaxAgeValid = dateOfBirth.isBetween(maxAgeYearsAgo, today);

    if (!isMaxAgeValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: maxAgeErrorMsg,
      });

      return z.NEVER;
    }
  };

const radioOptionsSchema = z
  .enum(['yes', 'no', ''])
  .superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: radio_input_error_warning,
      });
    }
  });

const signUpStep2Schema = z
  .object({
    formType: z.enum(['default', 'LEPartner', 'gift', 'redeem']),
    firstName: z.string().min(1, { message: firstName_input_error_warning }),
    lastName: z.string().min(1, { message: lastName_input_error_warning }),
    email: emailSchema,
    password: z.string().min(1, { message: password_input_error_warning }),
    confirmPassword: z
      .string()
      .min(1, { message: confirmPassword_input_error_warning }),
    city: z.object({
      name: z.string().min(1, { message: cityOfResidence_id_input_error }),
      id: z.number().min(1, { message: cityOfResidence_id_input_error }),
    }),
    countryCode: z.string(),
    phoneNumber: z
      .string()
      .min(1, { message: phoneNumber_input_error_warning }),

    dateOfBirth: z.string().superRefine(dobSuperRefinement()),
    children: z.object({
      shouldAdd: radioOptionsSchema,
      list: z
        .array(
          z.object({
            name: z
              .string()
              .min(1, { message: child_name_input_error_warning }),
            dob: z.string().superRefine(
              dobSuperRefinement({
                isOptional: false,
                maxAge: 18,
                maxAgeErrorMsg: child_name_max_age_error_warning,
              })
            ),
          })
        )
        .optional(),
    }),

    instagram: z
      .object({
        consent: radioOptionsSchema,
        username: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.consent === 'yes' && data.username?.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: instagram_username_input_error_warning,
            path: ['username'],
          });
        }
      }),

    voucher: z
      .object({
        code: z.string(),
        pin: z.string(),
      })
      .optional()
      .superRefine((voucher, ctx) => {
        if (!!voucher?.code?.length && !voucher?.pin?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please enter a pin',
            path: ['pin'],
          });
        }

        if (!!voucher?.pin.length && !voucher?.code?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please enter a code',
            path: ['code'],
          });
        }
      }),
    referralCode: z.string().optional(),
    promoCode: z
      .object({
        required: z.boolean(),
        coupon: z.string(),
        partnerCode: z.string(),
      })
      .optional(),
    whatsAppConsent: z.boolean().optional(),
    termsAccept: z.boolean().refine(value => value, {
      message: termsAccept_input_error_warning,
    }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: confirmPassword_input_error_warning,
    path: ['confirmPassword'],
  })
  .superRefine((data, ctx) => {
    const isPartner = data.formType === 'LEPartner';
    const isRedeem = data.formType === 'redeem';

    if (isPartner && !data.promoCode?.partnerCode && data.promoCode?.required) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a promo code',
        path: ['promoCode', 'partnerCode'],
      });
    }

    if (isRedeem && !data.voucher?.code?.length && !data.voucher?.pin?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a code',
        path: ['voucher', 'code'],
      });
    }
  });

const signUpStep3Schema = z.object({
  creditCard: z
    .object({
      brand: z.string(),
      name: z
        .string()
        .min(1, { message: credit_card_name_input_error_warning }),
      number: z
        .string()
        .min(1, { message: credit_card_number_input_error_warning }),
      expiry: z
        .string()
        .min(5, { message: credit_card_expiry_date_input_error_warning })
        .superRefine((date, ctx) => {
          const expiry = moment(date, 'MM/YY');
          if (!expiry.isValid()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: credit_card_expiry_date_input_error_warning,
            });
          } else if (expiry.isBefore(moment())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: credit_card_expiry_date_input_error_warning,
            });
          }
        }),
      cvc: z.string(),
    })
    .superRefine(({ brand, cvc }, ctx) => {
      if (brand === 'american-express') {
        if (cvc.length < 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please add a 4-digit security code',
            path: ['cvc'],
          });
        }
      } else {
        if (cvc.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please add a 3-digit security code',
            path: ['cvc'],
          });
        }
      }
    }),
});

const signUpSchema = z.intersection(
  signUpStep1Schema,
  z.intersection(signUpStep2Schema, signUpStep3Schema)
);

type SignUpSchema = z.infer<typeof signUpSchema>;

type SignUpStep1Schema = z.infer<typeof signUpStep1Schema>;
type SignUpStep2Schema = z.infer<typeof signUpStep2Schema>;
type SignUpStep3Schema = z.infer<typeof signUpStep3Schema>;

export {
  type SignUpSchema,
  type SignUpStep1Schema,
  type SignUpStep2Schema,
  type SignUpStep3Schema,
  signUpSchema,
  signUpStep1Schema,
  signUpStep2Schema,
  signUpStep3Schema,
};
